import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  ClinicAgentSocket,
  ClinicSocketState,
} from '../services/clinicAgentSocket';
import { clinicChatHistoryService } from '../services/clinicChatHistoryService';
import {
  applyProposal as applyProposalRest,
  ProfileIdToUserId,
} from '../services/clinicAgentProposals';
import { patientsService } from '../services/patientsService';
import {
  ChatMessage,
  Proposal,
  ProposalResolution,
  ProposalState,
  ProposalStatus,
} from '../models/clinicAgent';

type ClinicAgentContextValue = {
  messages: ChatMessage[];
  connectionState: ClinicSocketState;
  typing: boolean;
  statusLabel: string | null;
  isBusy: boolean;
  authError: boolean;
  sendMessage: (
    text: string,
    patientId?: number,
    image?: { dataUrl: string; type: string },
  ) => void;
  applyProposal: (messageId: string, proposalIndex: number) => Promise<void>;
  discardProposal: (messageId: string, proposalIndex: number) => void;
};

const ClinicAgentContext = createContext<ClinicAgentContextValue | undefined>(undefined);

const CLINICIAN_ROLES = ['doctor', 'nutritionist', 'admin'];

function makeId(): string {
  return `msg-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
}

// Map any server/normalized status onto our terminal lifecycle states.
function normalizeResolutionStatus(status: string): 'applied' | 'discarded' {
  const s = (status || '').toLowerCase();
  if (['discarded', 'rejected', 'declined', 'cancelled', 'canceled'].includes(s)) {
    return 'discarded';
  }
  return 'applied';
}

// Fold server-authored resolutions (proposal_resolved, broadcast to every tab)
// into the transcript. When the resolved proposal is present locally we update
// its badge in place (idempotent — covers the originating tab's optimistic
// update too); otherwise we append a standalone resolution card so actions from
// other devices still show. Deduped on proposal_id.
function applyResolutionsToMessages(
  messages: ChatMessage[],
  results: ProposalResolution[],
): ChatMessage[] {
  let next = messages;
  for (const res of results) {
    const status = normalizeResolutionStatus(res.status);
    let matched = false;
    next = next.map((m) => {
      if (m.runId !== res.run_id || !m.proposals) return m;
      const proposals = m.proposals.map((p) => {
        if (p.id !== res.proposal_id) return p;
        matched = true;
        return { ...p, status: status as ProposalStatus, error: undefined };
      });
      return matched ? { ...m, proposals } : m;
    });
    if (matched) continue;

    // No live proposal to update — render a resolution card unless we already
    // have one for this proposal_id (dedupe broadcast echoes / reload overlap).
    const exists = next.some(
      (m) => m.messageType === 'proposal_resolution' && m.resolution?.proposal_id === res.proposal_id,
    );
    if (exists) continue;
    next = [
      ...next,
      {
        id: makeId(),
        role: 'assistant',
        text: '',
        ts: Date.now(),
        messageType: 'proposal_resolution',
        resolution: { ...res, status },
      },
    ];
  }
  return next;
}

export const ClinicAgentProvider = ({ children }: { children: ReactNode }) => {
  const { user, access, refreshTokens } = useAuth();
  const isClinician = !!user && CLINICIAN_ROLES.includes(user.role);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [connectionState, setConnectionState] = useState<ClinicSocketState>('closed');
  const [typing, setTyping] = useState(false);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [authError, setAuthError] = useState(false);

  const activeRunIdRef = useRef<number | null>(null);
  const completedRunIdsRef = useRef<Set<number>>(new Set());
  // True between sending a message (or seeing typing/status) and the assistant
  // reply. A disconnect while awaiting means the turn is lost server-side, so we
  // clear the spinner and surface a notice instead of hanging forever.
  const awaitingResponseRef = useRef(false);
  const socketRef = useRef<ClinicAgentSocket | null>(null);
  const profileToUserIdRef = useRef<ProfileIdToUserId>(new Map());
  const historyLoadedRef = useRef(false);

  const refreshTokensRef = useRef(refreshTokens);
  useEffect(() => {
    refreshTokensRef.current = refreshTokens;
  }, [refreshTokens]);

  // Load the profile-id -> userId map (needed for activation / welcome-email).
  const loadPatientMap = useCallback(async () => {
    try {
      const res = await patientsService.getPatients(1, '');
      const map = profileToUserIdRef.current;
      (res.results || []).forEach((p) => map.set(p.id, p.userId));
    } catch (err) {
      console.warn('ClinicAgent: failed to preload patient map', err);
    }
  }, []);

  // Hydrate transcript from server history (GET /api/chat/) once per session.
  const loadHistory = useCallback(async () => {
    if (historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    try {
      const history = await clinicChatHistoryService.getHistory(1);
      if (history.length > 0) {
        setMessages((prev) => (prev.length === 0 ? history : prev));
      }
    } catch (err) {
      console.warn('ClinicAgent: failed to load chat history', err);
    }
  }, []);

  // Socket lifecycle — only for authenticated clinicians.
  useEffect(() => {
    if (!access || !isClinician) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      setConnectionState('closed');
      setTyping(false);
      setStatusLabel(null);
      activeRunIdRef.current = null;
      completedRunIdsRef.current.clear();
      return;
    }

    if (socketRef.current) return;

    setAuthError(false);
    loadHistory();
    loadPatientMap();

    const socket = new ClinicAgentSocket(
      () => localStorage.getItem('access') || undefined,
      {
        onStateChange: (s) => {
          setConnectionState(s);
          if (s === 'closed' || s === 'reconnecting') {
            setStatusLabel(null);
            activeRunIdRef.current = null;
            // A dropped connection loses any in-flight turn server-side: the
            // reply will never arrive. Stop the spinner and tell the clinician
            // to resend, rather than leaving "Escribiendo…" up forever.
            if (awaitingResponseRef.current) {
              awaitingResponseRef.current = false;
              setTyping(false);
              setMessages((prev) => [
                ...prev,
                {
                  id: makeId(),
                  role: 'assistant',
                  text: 'Se perdió la conexión y la respuesta no se completó. Reenvía tu mensaje cuando se restablezca la conexión.',
                  ts: Date.now(),
                  messageType: 'system_notice',
                },
              ]);
            }
          }
        },
        onTyping: () => {
          awaitingResponseRef.current = true;
          setTyping(true);
        },
        onAgentStatus: ({ label, run_id }) => {
          // Drop late status frames for an already-completed run (no ghost chips).
          if (run_id !== null && completedRunIdsRef.current.has(run_id)) return;
          awaitingResponseRef.current = true;
          activeRunIdRef.current = run_id;
          setStatusLabel(label);
        },
        onAssistantMessage: ({ text, proposals, run_id }) => {
          if (Number.isFinite(run_id)) completedRunIdsRef.current.add(run_id);
          awaitingResponseRef.current = false;
          setTyping(false);
          setStatusLabel(null);
          activeRunIdRef.current = null;
          const proposalStates: ProposalState[] = (proposals || []).map((p: Proposal) => ({
            ...p,
            status: 'pending',
          }));
          // Render even when text is empty but proposals exist.
          if (text.trim() === '' && proposalStates.length === 0) return;
          setMessages((prev) => [
            ...prev,
            {
              id: makeId(),
              role: 'assistant',
              text,
              proposals: proposalStates.length > 0 ? proposalStates : undefined,
              ts: Date.now(),
              runId: Number.isFinite(run_id) ? run_id : undefined,
            },
          ]);
        },
        onProactiveMessage: ({ text }) => {
          // Clinic agent shouldn't push, but render defensively if it ever does.
          awaitingResponseRef.current = false;
          setStatusLabel(null);
          activeRunIdRef.current = null;
          if (text.trim() === '') return;
          setMessages((prev) => [
            ...prev,
            { id: makeId(), role: 'assistant', text, ts: Date.now() },
          ]);
        },
        onProposalResolved: ({ results }) => {
          if (results.length === 0) return;
          setMessages((prev) => applyResolutionsToMessages(prev, results));
        },
        onProposalAck: ({ pending }) => {
          // Receipt confirmation for our proposal_result; nothing to render.
          console.debug('ClinicAgent: proposal_ack, pending =', pending);
        },
        onUnauthorized: () => {
          setAuthError(true);
        },
        refreshToken: async () => {
          try {
            const result = await refreshTokensRef.current();
            return !!result?.success;
          } catch (err) {
            console.warn('ClinicAgent: refreshTokens threw', err);
            return false;
          }
        },
      },
    );

    socketRef.current = socket;
    socket.connect();

    return () => {
      socket.close();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [access, isClinician, loadHistory, loadPatientMap]);

  // Web lifecycle: browsers drop idle WS in background tabs and freeze timers,
  // so on tab-visible / back-online refresh the token then force a reconnect.
  useEffect(() => {
    if (!access || !isClinician) return;

    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const socket = socketRef.current;
      if (!socket) return;
      // The connection usually survives a backgrounded tab. Don't tear down a
      // healthy socket on every refocus — that would needlessly drop an
      // in-flight turn. Only refresh the token and reconnect when it actually
      // died while we were away.
      if (socket.isOpen) return;
      try {
        await refreshTokensRef.current();
      } catch {
        /* fall through to reconnect anyway */
      }
      socket.forceReconnect();
    };
    const onOnline = () => socketRef.current?.forceReconnect();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [access, isClinician]);

  const sendMessage = useCallback(
    (text: string, patientId?: number, image?: { dataUrl: string; type: string }) => {
      const trimmed = text.trim();
      // Allow sending an image with no text, but never an empty message.
      if (!trimmed && !image) return;
      const socket = socketRef.current;
      if (!socket) return;
      setMessages((prev) => [
        ...prev,
        {
          id: makeId(),
          role: 'user',
          text: trimmed,
          ts: Date.now(),
          ...(image ? { imageUrl: image.dataUrl } : {}),
        },
      ]);
      awaitingResponseRef.current = true;
      setTyping(true);
      socket.send({
        type: 'message',
        text: trimmed,
        ...(patientId != null ? { patient_id: patientId } : {}),
        ...(image ? { file_data: image.dataUrl, file_type: image.type } : {}),
      });
    },
    [],
  );

  // Mutate a single proposal's lifecycle state within a message.
  const setProposalState = useCallback(
    (messageId: string, proposalIndex: number, patch: Partial<ProposalState>) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId || !m.proposals) return m;
          const proposals = m.proposals.map((p, i) =>
            i === proposalIndex ? { ...p, ...patch } : p,
          );
          return { ...m, proposals };
        }),
      );
    },
    [],
  );

  // Report a proposal outcome back to the agent. Needs both run_id and the
  // server-assigned proposal_id; without them the server can't mark the run.
  const reportResult = useCallback(
    (
      runId: number | undefined,
      proposalId: string | undefined,
      status: 'applied' | 'discarded',
      detail?: Record<string, unknown>,
    ) => {
      if (runId == null || !proposalId) return;
      socketRef.current?.send({
        type: 'proposal_result',
        results: [
          {
            run_id: runId,
            proposal_id: proposalId,
            status,
            ...(detail ? { detail } : {}),
          },
        ],
      });
    },
    [],
  );

  const ensureUserId = useCallback(async (profileId: number | null) => {
    if (profileId == null) return;
    if (profileToUserIdRef.current.has(profileId)) return;
    try {
      const patient = await patientsService.getPatientById(profileId);
      if (patient?.userId != null) {
        profileToUserIdRef.current.set(profileId, patient.userId);
      }
    } catch (err) {
      console.warn('ClinicAgent: failed to resolve userId for profile', profileId, err);
    }
  }, []);

  const applyProposal = useCallback(
    async (messageId: string, proposalIndex: number) => {
      const msg = messages.find((m) => m.id === messageId);
      const proposal = msg?.proposals?.[proposalIndex];
      if (!proposal) return;

      setProposalState(messageId, proposalIndex, { status: 'applying', error: undefined });
      try {
        // Lazily resolve AdminUser id for the two endpoints that need it.
        if (proposal.action === 'set_patient_activation' || proposal.action === 'send_welcome_email') {
          await ensureUserId(proposal.patient_id);
        }
        const detail = await applyProposalRest(proposal, profileToUserIdRef.current);
        setProposalState(messageId, proposalIndex, { status: 'applied' });
        reportResult(msg?.runId, proposal.id, 'applied', detail);
      } catch (err: any) {
        const message =
          err?.response?.detail ||
          (err?.response && typeof err.response === 'object'
            ? Object.values(err.response).flat().join(' ')
            : null) ||
          err?.message ||
          'No se pudo aplicar la propuesta.';
        setProposalState(messageId, proposalIndex, { status: 'error', error: String(message) });
      }
    },
    [messages, setProposalState, ensureUserId, reportResult],
  );

  const discardProposal = useCallback(
    (messageId: string, proposalIndex: number) => {
      const msg = messages.find((m) => m.id === messageId);
      const proposal = msg?.proposals?.[proposalIndex];
      if (!proposal) return;
      // Mark discarded in place (keeps the card with a "descartado" badge) and
      // report it so the agent and other tabs reflect the decision.
      setProposalState(messageId, proposalIndex, { status: 'discarded', error: undefined });
      reportResult(msg?.runId, proposal.id, 'discarded');
    },
    [messages, setProposalState, reportResult],
  );

  const isBusy = typing || statusLabel != null;

  const value = useMemo<ClinicAgentContextValue>(
    () => ({
      messages,
      connectionState,
      typing,
      statusLabel,
      isBusy,
      authError,
      sendMessage,
      applyProposal,
      discardProposal,
    }),
    [messages, connectionState, typing, statusLabel, isBusy, authError, sendMessage, applyProposal, discardProposal],
  );

  // Don't provide the agent UI for non-clinicians.
  if (!isClinician) {
    return <>{children}</>;
  }

  return <ClinicAgentContext.Provider value={value}>{children}</ClinicAgentContext.Provider>;
};

export const useClinicAgent = (): ClinicAgentContextValue | null => {
  return useContext(ClinicAgentContext) ?? null;
};
